import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Typography, Button } from "@material-ui/core";

// NPM
import { animated } from "react-spring";
import { useCookies } from "react-cookie";
import {
	CardNumberElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { ReactSketchCanvas } from "react-sketch-canvas";

// Components
import LoadingButton from "./LoadingButton.Component";
import GenericDialog from "./GenericDialog.Component";

// Classes
import AppointmentBookingRequest from "../../domain/classes/AppointmentBookingRequest";
import Journey from "../../domain/classes/Journey";
import Utility from "../../domain/classes/Utility";

// Errors
import UnableToProcessPaymentError from "../../domain/classes/errors/UnableToProcessPaymentError";
import UnableToInitiateStripeError, {
	UnableToInitiateStripeErrorMessage,
} from "../../domain/classes/errors/UnableToInitiateStripeError";
import MissingSignatureError from "../../domain/classes/errors/MissingSignatureError";

// Config
import {
	QV_MEDICAL_PHONE_NUMBER,
	EXTRA_AUTH_MESSAGE,
	SAD_COLOURED,
} from "../../domain/utility/config";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import { usePaymentManager } from "../../contexts/PaymentManager.Context";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		imgContainer: { height: 80, marginBottom: theme.spacing(2) },
		img: { height: "100%", margin: "auto", display: "block" },
	});

export interface ProcessPaymentButtonProps {}

const ProcessPaymentButton = React.forwardRef<
	HTMLDivElement,
	ProcessPaymentButtonProps & WithStyles<typeof styles>
>(({ classes }, ref) => {
	const [cookies] = useCookies();

	const stripe = useStripe();
	const elements = useElements();

	const {
		handleJourneyStageChange,
		journeyStage,
		stageResponse,
		handleStageResponseChange,
		clearCookies,
	} = useApplicationManager();
	const {
		cardHolder,
		canvasRef,
		handleErrorChange,
		handleCardFlippedChange,
	} = usePaymentManager();

	// States
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>("");

	// Step 1 of Stage Three Flow
	const checkSignature = async (): Promise<string> => {
		const signaturePad = (canvasRef as React.RefObject<ReactSketchCanvas>)
			.current as ReactSketchCanvas;

		const signature = await signaturePad.exportSvg();
		const sketchingTime = await signaturePad.getSketchingTime();
		const paths = await signaturePad.exportPaths();

		if (paths.length === 0 || sketchingTime < 100) {
			throw new MissingSignatureError();
		}
		return signature;
	};

	// Step 2 of Stage Three Flow
	const confirmCardWithStripe = async () => {
		if (!stripe || !elements) {
			throw new UnableToInitiateStripeError();
		}

		const cardNumberElement = elements.getElement(CardNumberElement);

		return await stripe.confirmCardSetup(
			stageResponse.clientSecret,
			{
				payment_method: {
					card: cardNumberElement as StripeCardNumberElement,
					billing_details: {
						name: cardHolder,
					},
				},
			},
			{ handleActions: true }
		);
	};

	// Step 3 of Stage Three Flow
	const processPayment = async (
		paymentMethodId: string,
		paymentIntentId: string,
		isAuthenticatePostBack: boolean = false,
		signature: string
	) => {
		try {
			let result = await AppointmentBookingRequest.startBookingStageThree(
				{
					stageId: cookies["stage_id"],
					signature,
					paymentMethodId: paymentMethodId,
					paymentIntentId: paymentIntentId,
					isAuthenticatePostBack: isAuthenticatePostBack,
				}
			);

			if (result.data.message === EXTRA_AUTH_MESSAGE) {
				await stripe?.handleCardAction(result.data.clientSecret);

				result = await AppointmentBookingRequest.startBookingStageThree(
					{
						stageId: cookies["stage_id"],
						signature,
						paymentMethodId: result.data.stripePaymentMethodId,
						paymentIntentId: result.data.stripePaymentIntentId,
						isAuthenticatePostBack: isAuthenticatePostBack,
					}
				);
			}

			if (result.status !== 200 || !result.data.status) {
				throw new UnableToProcessPaymentError();
			}

			return result;
		} catch (error) {
			console.log(error.message);
			throw new Error(error.message);
		}
	};

	// Handlers
	const handleNextButtonClick = async () => {
		setLoading(true);

		try {
			const signature = await checkSignature();

			const stripeResult = await confirmCardWithStripe();

			if (
				stripeResult &&
				stripeResult.setupIntent &&
				stripeResult.setupIntent.payment_method
			) {
				const result = await processPayment(
					stripeResult.setupIntent?.payment_method,
					"",
					false,
					signature
				);

				handleStageResponseChange(result.data);
			} else {
				throw new UnableToInitiateStripeError();
			}

			clearCookies();

			handleJourneyStageChange(Journey.goFrom(journeyStage, "forward"));
		} catch (error) {
			if (!(error instanceof MissingSignatureError)) {
				handleCardFlippedChange(false);
			}
			// Utility.debug(error.message);

			handleErrorChange(UnableToInitiateStripeErrorMessage);
		}
		setLoading(false);
	};

	const handleDialogClose = () => {
		setError("");
	};

	return (
		<React.Fragment>
			<LoadingButton
				fullWidth
				onClick={handleNextButtonClick}
				isLoading={loading}
			>
				Pay
			</LoadingButton>
			<GenericDialog
				open={Boolean(error)}
				title="An error has occured."
				content={
					<React.Fragment>
						<div className={classes.imgContainer}>
							<img
								src={SAD_COLOURED}
								className={classes.img}
								alt="Unable to process booking stage 2."
							/>
						</div>
						<Typography
							variant="body1"
							component="p"
							align="center"
						>
							{error}
						</Typography>
					</React.Fragment>
				}
				actions={
					<React.Fragment>
						<Button
							onClick={() => {
								handleDialogClose();
							}}
							color="primary"
						>
							Close
						</Button>
						<Button
							onClick={() => {
								window.open(`tel:${QV_MEDICAL_PHONE_NUMBER}`);
							}}
							color="primary"
							variant="contained"
						>
							Call
						</Button>
					</React.Fragment>
				}
			/>
		</React.Fragment>
	);
});

const StyledProcessPaymentButton = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("ProcessPaymentButton"),
})(ProcessPaymentButton);

export default StyledProcessPaymentButton;

export const AnimatedProcessPaymentButton = animated(
	StyledProcessPaymentButton
);
